import { Bell, User, LogOut, Coins, CreditCard, ArrowRight } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { formatNumber } from '@/utils/format'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

export default function TopNav() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [showUserMenu, setShowUserMenu] = useState(false)

  // Fetch unread message count
  const { data: unreadData } = useQuery({
    queryKey: ['support-unread'],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const response = await axios.get('/api/support/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      })
      return response.data
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: !!user, // Only fetch if user is logged in
  })

  const unreadCount = unreadData?.data?.count || 0

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-800">Hello, {user?.first_name}!</h2>
          </div>

          <div className="flex items-center space-x-6">
            {/* Barter Coin Balance - Hidden on mobile (shown in dropdown instead) */}
            <div
              onClick={() => navigate('/dashboard/coins')}
              className="hidden md:flex items-center space-x-2 px-4 py-2 bg-primary-50 rounded-lg cursor-pointer hover:bg-primary-100 transition-colors"
            >
              <Coins className="w-5 h-5 text-primary-600" />
              <div className="text-right">
                <p className="text-xs text-gray-600">Barter Coins</p>
                <p className="text-lg font-bold text-primary-700">
                  {formatNumber(user?.barter_coin_balance || 0)}
                </p>
              </div>
            </div>

            {/* Notifications */}
            <button
              onClick={() => navigate('/dashboard/support')}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
              title={unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'No unread messages'}
            >
              <Bell className="w-6 h-6" />
              {unreadCount > 0 && (
                <>
                  <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                </>
              )}
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                  {user?.first_name.charAt(0)}{user?.last_name.charAt(0)}
                </div>
                <div className="text-left">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    {user?.subscription_tier === 'paid' && user?.subscription_status === 'active' && (
                      <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded">Pro</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 capitalize">
                    {user?.user_type.replace('_', ' ')}
                  </p>
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {/* Barter Coins - Only visible on mobile */}
                  <button
                    onClick={() => {
                      navigate('/dashboard/coins')
                      setShowUserMenu(false)
                    }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 md:hidden"
                  >
                    <div className="flex items-center space-x-2">
                      <Coins className="w-4 h-4 text-primary-600" />
                      <span className="text-gray-700 font-medium">Barter Coins</span>
                    </div>
                    <span className="text-lg font-bold text-primary-700">
                      {formatNumber(user?.barter_coin_balance || 0)}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      navigate('/dashboard/profile')
                      setShowUserMenu(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <User className="w-4 h-4" />
                    <span>My Profile</span>
                  </button>
                  <button
                    onClick={() => {
                      logout()
                      setShowUserMenu(false)
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subscription Banner for Free Tier */}
        {user?.user_type === 'professional' && user.subscription_status !== 'active' && (
          <div className="mt-4 p-4 bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <CreditCard className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-primary-900">
                    You're on the Free Tier
                  </p>
                  <p className="text-xs text-primary-700 mt-1">
                    Upgrade to unlock unlimited barters, priority support, and premium features for just $5/month
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/dashboard/subscription')}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap ml-4"
              >
                <span>Upgrade Now</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
